import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import {
  ISKUForm,
  ISKUDeviceData,
  modelLabelMap,
  ptPrefillOptions,
} from "../variables/skuData";

interface SKUFormItemProps {
  index: number;
  device: ISKUDeviceData;
  deviceTypeOptions: string[];
  getModelsToRunForDeviceType: (type: string) => string[];
  getHasPanTiltForDeviceType: (type: string) => boolean;
  onRemove: (index: number) => void;
  control: Control<ISKUForm>;
  errors: FieldErrors<ISKUForm>;
  watchedDeviceType: string;
  setValue: UseFormSetValue<ISKUForm>;
  watch: UseFormWatch<ISKUForm>;
}

const SKUFormItem = ({
  index,
  device,
  deviceTypeOptions,
  getModelsToRunForDeviceType,
  getHasPanTiltForDeviceType,
  onRemove,
  control,
  errors,
  watchedDeviceType,
  setValue,
  watch,
}: SKUFormItemProps): JSX.Element => {
  // Check if the selected device type supports PT
  const deviceSupportsPT = getHasPanTiltForDeviceType(watchedDeviceType);
  // Watch isPT value for this device to conditionally show PT options
  const isPTEnabled = watch(`skuDataConfigurations.${index}.isPT`);

  return (
    <Grid
      container
      spacing={2}
      alignItems="center"
      sx={{
        borderRadius: "8px",
        padding: "1em",
        mb: 3,
      }}
    >
      <Grid item>
        <Typography sx={{ minWidth: "70px" }}>Device {index + 1}</Typography>
      </Grid>
      <Grid item>
        <Controller
          name={`skuDataConfigurations.${index}.deviceType`}
          control={control}
          defaultValue={device.deviceType}
          rules={{
            required: "Device Type is required",
          }}
          render={({ field }) => (
            <TextField
              select
              label="Device Type"
              value={field.value}
              sx={{ minWidth: 180, position: "relative" }}
              onChange={e => {
                field.onChange(e);

                // Pre-populate with ALL available models for this device type
                const selectedDeviceType = e.target.value;
                const allModelsForType =
                  getModelsToRunForDeviceType(selectedDeviceType);

                setValue(
                  `skuDataConfigurations.${index}.model`,
                  allModelsForType
                );

                // Reset PT fields when device type changes
                setValue(`skuDataConfigurations.${index}.isPT`, false);

                setValue(
                  `skuDataConfigurations.${index}.panTiltType`,
                  undefined
                );

                setValue(`skuDataConfigurations.${index}.ptPrefill`, undefined);
              }}
              error={!!errors?.skuDataConfigurations?.[index]?.deviceType}
              helperText={
                errors?.skuDataConfigurations?.[index]?.deviceType
                  ? "Device Type is required"
                  : " "
              }
              FormHelperTextProps={{
                sx: { position: "absolute", bottom: -22 },
              }}
            >
              <MenuItem value="">
                <em>Select Device Type</em>
              </MenuItem>
              {deviceTypeOptions.map(type => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>
      <Grid item>
        <Controller
          name={`skuDataConfigurations.${index}.model`}
          control={control}
          defaultValue={device.model || []}
          rules={{
            validate: value => {
              const validModels = (value || []).filter(
                (m: string) => m && m.trim() !== ""
              );

              return validModels.length > 0 || "At least one model is required";
            },
          }}
          render={({ field }) => (
            <TextField
              select
              label="Model(s)"
              value={field.value}
              onChange={event =>
                field.onChange(
                  (event.target.value as unknown as string[]).filter(
                    val => val.trim() !== ""
                  )
                )
              }
              SelectProps={{
                multiple: true,
                renderValue: selected =>
                  (selected as string[])
                    .filter(model => model.trim() !== "")
                    .map(model => modelLabelMap[model] || model)
                    .join(", "),
              }}
              sx={{ minWidth: 220, position: "relative" }}
              error={!!errors?.skuDataConfigurations?.[index]?.model}
              helperText={
                errors?.skuDataConfigurations?.[index]?.model?.message || " "
              }
              FormHelperTextProps={{
                sx: { position: "absolute", bottom: -22 },
              }}
            >
              {getModelsToRunForDeviceType(watchedDeviceType).map(model => (
                <MenuItem key={model} value={model}>
                  <Checkbox
                    checked={field.value?.includes(model)}
                    sx={{ marginRight: 1 }}
                  />
                  {modelLabelMap[model] || model}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>
      {/* PT Options - Only show if device type supports PT */}
      {deviceSupportsPT && (
        <>
          <Grid item>
            <Controller
              name={`skuDataConfigurations.${index}.isPT`}
              control={control}
              defaultValue={device.isPT || false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value || false}
                      onChange={e => {
                        field.onChange(e.target.checked);

                        // Clear PT fields when disabling PT
                        if (!e.target.checked) {
                          setValue(
                            `skuDataConfigurations.${index}.panTiltType`,
                            undefined
                          );

                          setValue(
                            `skuDataConfigurations.${index}.ptPrefill`,
                            undefined
                          );
                        }
                      }}
                    />
                  }
                  label="Has PT"
                />
              )}
            />
          </Grid>
          {isPTEnabled && (
            <>
              <Grid item>
                <Controller
                  name={`skuDataConfigurations.${index}.ptPrefill`}
                  control={control}
                  defaultValue={device.ptPrefill || undefined}
                  render={({ field }) => (
                    <TextField
                      select
                      label="PT Options"
                      value={field.value || ""}
                      onChange={field.onChange}
                      sx={{ minWidth: 150, position: "relative" }}
                      helperText=" "
                      FormHelperTextProps={{
                        sx: { position: "absolute", bottom: -22 },
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Option</em>
                      </MenuItem>
                      {ptPrefillOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            </>
          )}
        </>
      )}
      <Grid item>
        <Button
          variant="outlined"
          color="error"
          onClick={() => onRemove(index)}
        >
          Remove
        </Button>
      </Grid>
    </Grid>
  );
};

export default SKUFormItem;
